// Dashboard principal
import * as React from "react";
import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "../config/axiosConfig";
import moment from "moment";
import "moment/locale/es";
import DashboardLayout from "../layouts/DashboardLayout";
import { Link as RouterLink, useNavigate } from "react-router-dom"; // Importado: useNavigate
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Skeleton,
  Tooltip,
  Alert,
  IconButton,
  Link as MuiLink,
  Container,
  LinearProgress,
  Divider,
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import { keyframes } from "@emotion/react";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import NewspaperIcon from "@mui/icons-material/Newspaper";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import FolderIcon from "@mui/icons-material/Folder";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import RefreshIcon from "@mui/icons-material/Refresh";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

// 👇 Imports para el FAB flotante (SpeedDial)
import Backdrop from '@mui/material/Backdrop'; 
import SpeedDial from "@mui/material/SpeedDial";
import SpeedDialAction from "@mui/material/SpeedDialAction";
import SpeedDialIcon from "@mui/material/SpeedDialIcon";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import ENV from "../config/env";
import { splitTituloContenido, esVideoNoticia, esImagenNoticia, extraerYoutubeId, textoParaMostrar, extraerPlayStore } from "../utils/noticiasHelpers";
import YoutubeEmbed from "../components/news/YoutubeEmbed";
import PlayStoreCard from "../components/news/PlayStoreCard";
import NuevoAnuncioDialog from "../components/dashboard/NuevoAnuncioDialog";
import {
  CalendarioEquipoProvider,
  CalendarioMiniGrid,
  CalendarioDetalleDia,
} from "../modules/permisos/components/DashboardCalendarioWidget";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { listarCentroEquipos } from "../modules/centroEquipos/services/centroEquiposService";

moment.locale("es");

// Definición de las acciones para el SpeedDial
const speedDialActions = [
    { 
        key: "emitir-requerimiento", 
        icon: <ReceiptLongIcon />, 
        name: "Emitir Requerimiento", 
        to: "/requerimiento" 
    },
    { 
        key: "emitir-reclamo", 
        icon: <ReportProblemIcon />, 
        name: "Emitir Reclamo", 
        to: "/reclamos" 
    },
];

// Utilidades
const joinUrl = (base, path) => {
  if (!base || !path) return undefined;
  return base.replace(/\/+$/, "") + "/" + String(path).replace(/^\/+/, "");
};

const firstChar = (s, fallback = "?") => (s ? String(s).trim().charAt(0).toUpperCase() : fallback);

// Estilo del contenedor principal
const Page = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(10), // deja aire para el botón flotante de acciones rápidas
}));

// Componente reutilizable para skeletons
const LoadingSkeleton = ({ count = 3, height = 60, variant = "rounded" }) => (
  <Stack spacing={1}>
    {Array.from({ length: count }).map((_, idx) => (
      <Skeleton key={idx} variant={variant} height={height} />
    ))}
  </Stack>
);

// Tarjeta de métrica: fondo neutro y el color solo en el ícono, para que las cuatro no compitan entre sí.
const MetricCard = ({ title, value, icon, color = "primary", onClick }) => {
  const isClickable = Boolean(onClick);

  return (
    <Card
      variant="outlined"
      onClick={onClick}
      sx={{
        height: "100%",
        cursor: isClickable ? "pointer" : "default",
        transition: "border-color 0.15s ease, box-shadow 0.15s ease",
        ...(isClickable && {
          "&:hover": { borderColor: `${color}.main`, boxShadow: 3 },
          "&:hover .metric-ir": { color: `${color}.main`, transform: "translateX(2px)" },
        }),
      }}
    >
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            aria-hidden
            sx={{
              width: 48,
              height: 48,
              flexShrink: 0,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              color: `${color}.main`,
              bgcolor: (theme) => alpha(theme.palette[color].main, 0.12),
            }}
          >
            {icon}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" color="text.secondary" noWrap>
              {title}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.15, fontVariantNumeric: "tabular-nums" }}>
              {value ?? <Skeleton width={48} />}
            </Typography>
          </Box>
          {isClickable && (
            <ChevronRightIcon className="metric-ir" sx={{ color: "text.disabled", transition: "all 0.15s ease" }} />
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

// Tarjeta de sección
const SectionCard = ({ title, subheader, action, children, maxHeight = 440, sx }) => (
  <Card
    variant="outlined"
    sx={{
      height: "100%",
      display: "flex",
      flexDirection: "column",
      ...sx,
    }}
  >
    <CardHeader
      title={
        <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
          {title}
        </Typography>
      }
      subheader={subheader}
      subheaderTypographyProps={{ variant: "caption" }}
      action={action}
      sx={{
        py: 1.5,
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        "& .MuiCardHeader-action": { alignSelf: "center", m: 0 },
      }}
    />
    <CardContent
      sx={{
        pt: 2,
        flex: 1,
        overflowY: "auto",
        maxHeight,
        "&::-webkit-scrollbar": { width: 6 },
        "&::-webkit-scrollbar-thumb": { bgcolor: "divider", borderRadius: 3 },
      }}
    >
      {children}
    </CardContent>
  </Card>
);

// Noticias
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Camión del MetricCard "Equipos por recibir" — mismo vaivén sutil que tenía en
// EquiposTransicionWidget.jsx, solo que acá el ícono es chico (va dentro del Avatar).
const enCamino = keyframes`
  0%   { transform: translateX(-3px); }
  50%  { transform: translateX(3px); }
  100% { transform: translateX(-3px); }
`;

const NewsCard = ({
  usuario, fecha, contenido, titulo, archivoUrl, sucursal, id, index = 0, profilePicture,
  documentoCarpetaId, documentoCarpetaNombre,
}) => {
  const relative = useMemo(() => (fecha ? moment(fecha).fromNow() : ""), [fecha]);
  const texto = textoParaMostrar(contenido);
  const playStore = extraerPlayStore(contenido);
  return (
    <Box
      component="article"
      sx={{
        animation: `${fadeInUp} 0.35s ease-out both`,
        animationDelay: `${Math.min(index, 5) * 80}ms`,
        "@media (prefers-reduced-motion: reduce)": { animation: "none" },
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1 }}>
        <Avatar
          src={profilePicture ? joinUrl(ENV.API_BASE_URL, profilePicture) : undefined}
          sx={{ width: 32, height: 32, fontSize: "0.9rem", bgcolor: "action.selected", color: "text.secondary" }}
          aria-label={`Avatar de ${usuario || "Usuario"}`}
        >
          {firstChar(usuario)}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
            {usuario || "Usuario"}
          </Typography>
          <Tooltip title={moment(fecha).format("DD/MM/YYYY HH:mm")}>
            <Typography variant="caption" color="text.secondary">
              {relative}
            </Typography>
          </Tooltip>
        </Box>
        <Chip size="small" variant="outlined" label={sucursal || "—"} aria-label={`Sucursal ${sucursal || "No especificada"}`} />
      </Stack>

      {titulo && (
        <MuiLink
          component={RouterLink}
          to={`/noticias/${id}`}
          underline="hover"
          color="text.primary"
          variant="subtitle2"
          sx={{ display: "block", fontWeight: 700, mb: 0.25 }}
        >
          {titulo}
        </MuiLink>
      )}
      {texto && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            whiteSpace: "pre-wrap",
          }}
        >
          {texto}
        </Typography>
      )}
      <Box>
        <YoutubeEmbed videoId={extraerYoutubeId(contenido)} sx={{ mt: 1.5 }} />
        {playStore && <PlayStoreCard {...playStore} compacta sx={{ mt: 1.5 }} />}
        {esVideoNoticia(archivoUrl) && (
          <Box
            component="video"
            src={`${ENV.API_BASE_URL}${archivoUrl}`}
            controls
            preload="metadata"
            playsInline
            sx={{ display: "block", width: "100%", maxHeight: 260, bgcolor: "common.black", borderRadius: 1, mt: 1.5 }}
          />
        )}
        {esImagenNoticia(archivoUrl) && (
          <Box
            component={RouterLink}
            to={`/noticias/${id}`}
            aria-label="Abrir noticia"
            sx={{ display: "block", mt: 1.5, borderRadius: 1, overflow: "hidden", bgcolor: "action.hover" }}
          >
            <Box
              component="img"
              src={`${ENV.API_BASE_URL}${archivoUrl}`}
              alt={titulo || "Imagen de la noticia"}
              loading="lazy"
              sx={{ display: "block", width: "100%", maxHeight: 260, objectFit: "cover" }}
            />
          </Box>
        )}
      </Box>

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.25 }}>
        {documentoCarpetaId && (
          <Chip
            size="small"
            variant="outlined"
            color="primary"
            icon={<FolderIcon sx={{ fontSize: 16 }} />}
            label={documentoCarpetaNombre || "Ver documentos"}
            component={RouterLink}
            to={`/documentos/${documentoCarpetaId}`}
            clickable
          />
        )}
        {archivoUrl && !esVideoNoticia(archivoUrl) && !esImagenNoticia(archivoUrl) && (
          <Chip
            size="small"
            variant="outlined"
            icon={<AttachFileIcon sx={{ fontSize: 16 }} />}
            label="Archivo"
            component="a"
            href={`${ENV.API_BASE_URL}${archivoUrl}`}
            target="_blank"
            rel="noopener noreferrer"
            clickable
          />
        )}
        <MuiLink component={RouterLink} to={`/noticias/${id}`} underline="hover" variant="body2" sx={{ ml: "auto" }}>
          Ver más
        </MuiLink>
      </Stack>
    </Box>
  );
};

export default function DashboardPage() {
  const navigate = useNavigate(); // 👈 Hook para la navegación

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [data, setData] = useState(null);
  const [profile, setProfile] = useState(null);
  
  // Estado para manejar la apertura del SpeedDial
  const [speedDialOpen, setSpeedDialOpen] = React.useState(false);
  const handleSpeedDialOpen = () => setSpeedDialOpen(true);
  const handleSpeedDialClose = () => setSpeedDialOpen(false);

  // Función de manejo del click para acciones de SpeedDial
  const handleActionClick = (path) => {
    handleSpeedDialClose(); // 1. Cerramos el menú
    navigate(path); // 2. Navegamos a la ruta
  };

  const fechaHoy = useMemo(() => moment().format("DD/MM/YYYY"), []);
  // "Lunes 27 de octubre" para el saludo del encabezado.
  const fechaLarga = useMemo(() => {
    const t = moment().format("dddd D [de] MMMM");
    return t.charAt(0).toUpperCase() + t.slice(1);
  }, []);

  const fetchResumen = useCallback(async () => {
    setErr("");
    setLoading(true);
    try {
      const { data } = await axios.get("/api/dashboard/resumen");
      setData(data);
    } catch (e) {
      console.error(e);
      setErr(e.response?.data?.message || "No se pudo cargar el dashboard.");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProfile = useCallback(async () => {
    try {
      const { data } = await axios.get("/api/auth/profile");
      setProfile(data);
    } catch (e) {
      console.warn("No se pudo obtener el perfil:", e?.response?.data || e.message);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
    fetchResumen();
  }, [fetchProfile, fetchResumen]);

  // "Equipos por recibir" (estado "Transicion" hacia la sucursal del usuario, ver
  // ListadoEquipos.jsx botón "Recibido") — antes vivía en su propio SectionCard con
  // EquiposTransicionWidget.jsx; ahora se pide acá mismo, más liviano (solo el total),
  // para mostrarlo como un MetricCard más entre las métricas principales.
  const [equiposTransicion, setEquiposTransicion] = useState(null);
  useEffect(() => {
    if (!profile?.sucursal) return;
    listarCentroEquipos({ tipo: "todos", estado: "Transicion", sucursal: profile.sucursal, pageSize: 1 })
      .then((res) => setEquiposTransicion(Number(res.data?.total || 0)))
      .catch(() => setEquiposTransicion(0));
  }, [profile?.sucursal]);

  // Anuncio nuevo: se muestra como alerta la primera vez que aparece un anuncio
  // que el usuario aún no vio (recordado en localStorage por usuario).
  const [anuncioNuevo, setAnuncioNuevo] = useState(null);

  useEffect(() => {
    if (!profile?.username || !data?.noticias?.length) return;
    const ultimo = data.noticias[0];
    const key = `anuncio_visto_${profile.username}`;
    const visto = localStorage.getItem(key);
    if (String(ultimo.id) !== visto) {
      setAnuncioNuevo(ultimo);
    }
  }, [profile, data]);

  const handleCerrarAnuncioNuevo = () => {
    if (anuncioNuevo && profile?.username) {
      localStorage.setItem(`anuncio_visto_${profile.username}`, String(anuncioNuevo.id));
    }
    setAnuncioNuevo(null);
  };

  const ranking = useMemo(() => {
    const rows = data?.solicitudes_por_usuario || [];
    const max = rows.reduce((m, r) => Math.max(m, Number(r.cantidad || 0)), 0) || 1;
    return rows.map((r) => ({
      key: r.usu,
      name: r.nombre || r.usu,
      avatar: r.profile_picture,
      count: Number(r.cantidad || 0),
      pct: Math.round((Number(r.cantidad || 0) / max) * 100),
    }));
  }, [data]);

  return (
    <DashboardLayout>
      <NuevoAnuncioDialog
        noticia={anuncioNuevo}
        open={Boolean(anuncioNuevo)}
        onClose={handleCerrarAnuncioNuevo}
      />
      <Page>
        <Container maxWidth="xl">
          {/* Header */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
              <Avatar
                src={joinUrl(ENV.API_BASE_URL,profile?.profilePicture)}
                sx={{ width: 52, height: 52, border: 2, borderColor: "background.paper", boxShadow: 2 }}
                aria-label={`Avatar de ${profile?.username || "Usuario"}`}
              >
                {firstChar(profile?.username, "U")}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2 }} noWrap>
                  Hola, {profile?.username || "Usuario"}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, minWidth: 0 }}>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {fechaLarga}
                  </Typography>
                  {profile?.sucursal && <Chip size="small" label={profile.sucursal} />}
                </Stack>
              </Box>
            </Stack>
            <Tooltip title="Recargar datos del dashboard">
              <span>
                <IconButton onClick={fetchResumen} disabled={loading} aria-label="Recargar datos del dashboard">
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          {err && <Alert severity="error" sx={{ mb: 3 }}>{err}</Alert>}

          {/* Métricas principales */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <MetricCard
                title="Mis OT pendientes"
                value={data?.ot_pendientes_usuario}
                icon={<AssignmentTurnedInIcon />}
                color="error"
                onClick={() => navigate("/orden-de-trabajo?tab=2")}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <MetricCard
                title="Mis requerimientos activos"
                value={data?.total_solicitudes_usuario}
                icon={<PlaylistAddCheckIcon />}
                color="warning"
                onClick={() =>
                  navigate("/requerimiento", {
                    state: { tab: 3 }, // 👈 abrir 4ta pestaña
                  })
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <MetricCard
                title="Equipos por recibir"
                value={equiposTransicion}
                icon={
                  <LocalShippingIcon
                    sx={{
                      animation: equiposTransicion > 0 ? `${enCamino} 1.6s ease-in-out infinite` : "none",
                      "@media (prefers-reduced-motion: reduce)": { animation: "none" },
                    }}
                  />
                }
                color="info"
                onClick={() =>
                  navigate(`/centro-equipos?tab=1&tipo=todos&estado=Transicion&sucursal=${encodeURIComponent(profile?.sucursal || "")}`)
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <MetricCard
                title="Mis clientes"
                value={data?.mis_clientes}
                icon={<PeopleAltIcon />}
                color="success"
                onClick={() => navigate("/mis-clientes")}
              />
            </Grid>
          </Grid>

          {/* Columna principal (calendario y solicitudes) + columna lateral de noticias.
              Calendario y detalle del día comparten estado via CalendarioEquipoProvider. */}
          <Grid container spacing={3} alignItems="flex-start">
            <Grid size={{ xs: 12, lg: 8 }}>
              <Grid container spacing={3} alignItems="stretch">
                <CalendarioEquipoProvider sucursal={profile?.sucursal}>
                  <Grid size={{ xs: 12, md: 5 }}>
                    <SectionCard
                      title="Calendario"
                      subheader="Permisos y coordinaciones"
                      action={
                        <Chip
                          size="small"
                          label="Ver todo"
                          component={RouterLink}
                          to="/permisos/calendario"
                          clickable
                          aria-label="Ver calendario completo"
                        />
                      }
                      maxHeight={460}
                    >
                      <CalendarioMiniGrid />
                    </SectionCard>
                  </Grid>

                  <Grid size={{ xs: 12, md: 7 }}>
                    <SectionCard title="Detalle del día" subheader="Del calendario" maxHeight={460}>
                      <CalendarioDetalleDia />
                    </SectionCard>
                  </Grid>
                </CalendarioEquipoProvider>

                <Grid size={{ xs: 12, md: 6 }}>
                  <SectionCard
                    title="Solicitudes pendientes"
                    subheader={`Hoy ${fechaHoy}`}
                    action={
                      <Chip
                        size="small"
                        label="Ver todo"
                        component={RouterLink}
                        to="/solicitudes"
                        clickable
                        aria-label="Ver todas las solicitudes"
                      />
                    }
                    maxHeight={420}
                  >
                    {loading ? (
                      <LoadingSkeleton count={2} height={48} />
                    ) : data?.solicitudes_pendientes?.length ? (
                      <List dense disablePadding>
                        {data.solicitudes_pendientes.map((s, idx) => (
                          <ListItem
                            key={s.id}
                            disableGutters
                            alignItems="flex-start"
                            divider={idx < data.solicitudes_pendientes.length - 1}
                            sx={{ py: 1 }}
                          >
                            <ListItemAvatar sx={{ minWidth: 48 }}>
                              <Avatar
                                aria-label="Solicitud pendiente"
                                sx={{
                                  width: 34,
                                  height: 34,
                                  color: "warning.main",
                                  bgcolor: (theme) => alpha(theme.palette.warning.main, 0.14),
                                }}
                              >
                                <NotificationsActiveIcon fontSize="small" />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {s.detalle}
                                </Typography>
                              }
                              secondary={
                                <Typography component="span" variant="caption" color="text.secondary">
                                  {moment(s.fecha).format("DD/MM/YYYY")} • Asignado a: {s.usuarios || "—"}
                                </Typography>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Sin solicitudes pendientes.
                      </Typography>
                    )}
                  </SectionCard>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <SectionCard
                    title="Ranking de solicitudes"
                    subheader="Por usuario, estados activos"
                    action={<TrendingUpIcon color="action" />}
                    maxHeight={420}
                  >
                    {loading ? (
                      <LoadingSkeleton count={3} height={48} />
                    ) : ranking.length ? (
                      <Stack spacing={1.75}>
                        {ranking.map((u, idx) => (
                          <Stack key={u.key} direction="row" spacing={1.5} alignItems="center">
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ width: 16, textAlign: "right", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}
                            >
                              {idx + 1}
                            </Typography>
                            <Avatar
                              src={joinUrl(ENV.API_BASE_URL, u.avatar)}
                              sx={{ width: 32, height: 32, fontSize: "0.9rem", bgcolor: "action.selected", color: "text.secondary" }}
                              aria-label={`Avatar de ${u.name}`}
                            >
                              {firstChar(u.name)}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                                  {u.name}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums", ml: 1 }}>
                                  {u.count}
                                </Typography>
                              </Stack>
                              <LinearProgress
                                variant="determinate"
                                value={u.pct}
                                sx={{
                                  height: 6,
                                  borderRadius: 999,
                                  bgcolor: "action.hover",
                                  "& .MuiLinearProgress-bar": { borderRadius: 999, bgcolor: "primary.main" },
                                }}
                                aria-label={`Progreso de ${u.name}: ${u.pct}%`}
                              />
                            </Box>
                          </Stack>
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Sin datos disponibles.
                      </Typography>
                    )}
                  </SectionCard>
                </Grid>
              </Grid>
            </Grid>

            {/* Noticias: columna lateral que acompaña el scroll en pantallas grandes; en celular va primero. */}
            <Grid
              size={{ xs: 12, lg: 4 }}
              sx={{ order: { xs: -1, lg: 0 }, position: { lg: "sticky" }, top: { lg: 16 } }}
            >
              <SectionCard
                title="Últimas noticias"
                subheader="Lo más reciente del sistema"
                action={
                  <Chip
                    size="small"
                    icon={<NewspaperIcon />}
                    label="Ver todas"
                    component={RouterLink}
                    to="/noticias"
                    clickable
                    aria-label="Ver todas las noticias"
                  />
                }
                maxHeight="calc(100vh - 160px)"
              >
                {loading ? (
                  <LoadingSkeleton count={3} height={120} />
                ) : Array.isArray(data?.noticias) && data.noticias.length ? (
                  <Stack spacing={2.5} divider={<Divider flexItem />}>
                    {data.noticias.slice(0, 4).map((n, idx) => {
                      const { titulo, cuerpo } = splitTituloContenido(n);
                      return (
                        <NewsCard
                          key={n.id || idx}
                          index={idx}
                          usuario={n.usuario}
                          fecha={n.fecha}
                          titulo={titulo}
                          contenido={cuerpo}
                          archivoUrl={n.archivo_url}
                          sucursal={n.sucursal}
                          id={n.id}
                          profilePicture={n.profile_picture}
                          documentoCarpetaId={n.documento_carpeta_id}
                          documentoCarpetaNombre={n.documento_carpeta_nombre}
                        />
                      );
                    })}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No hay noticias recientes.
                  </Typography>
                )}
              </SectionCard>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, textAlign: "right" }}>
            <MuiLink component={RouterLink} to="/metricas" underline="hover" aria-label="Ver reportes y métricas">
              Ver reportes y más métricas →
            </MuiLink>
          </Box>
        </Container>
      </Page>

      {/* SpeedDial flotante con Tooltips Persistentes */}
      <Backdrop open={speedDialOpen} sx={{ zIndex: (theme) => theme.zIndex.tooltip }} />
      <SpeedDial
        ariaLabel="Acciones rápidas"
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          zIndex: (theme) => theme.zIndex.tooltip + 1,
        }}
        icon={<SpeedDialIcon />}
        onClose={handleSpeedDialClose}
        onOpen={handleSpeedDialOpen}
        open={speedDialOpen}
        direction="up"
        FabProps={{
          color: "primary",
        }}
      >
        {speedDialActions.map((action) => (
          <SpeedDialAction
            key={action.key}
            icon={action.icon}
            tooltipTitle={action.name}
            // 👇 Función de click corregida para navegación
            onClick={() => handleActionClick(action.to)} 
            
            // Implementación de Persistent Tooltip
            slotProps={{
                tooltip: {
                    open: true, 
                    title: action.name,
                    placement: 'left',
                },
            }}
          />
        ))}
      </SpeedDial>
    </DashboardLayout>
  );
}
  